import React, { useState } from "react";
import { Tooltip, IconButton, Box, Modal, CircularProgress } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Visibility, Download } from "@mui/icons-material";
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import LeadsTable from "../Components/LeadsTable";
import { AtsCandidate } from "./AtsCandidates";
import { useResumeHandler } from "./useResumeHandler";

const PotentialTag: React.FC<{ potential: "High" | "Med" | "Low" }> = ({
    potential,
}) => {
    let color, icon;

    if (potential === "High") {
        color = "#2e7d32";
        icon = <ArrowUpwardIcon fontSize="small" />;
    } else if (potential === "Med") {
        color = "#DC6803";
        icon = <ArrowUpwardIcon fontSize="small" />;
    } else if (potential === "Low") {
        color = "#c62828";
        icon = <ArrowDownwardIcon fontSize="small" />;
    }

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 8px",
                borderRadius: "16px",
                color,
                border: `1px solid ${color}`,
                fontSize: "0.75rem",
                fontWeight: 500,
            }}
        >
            {potential} Potential {icon}
        </div>
    );
};

interface AtsLeadsTableProps {
    leads: AtsCandidate[];
    totalProfiles: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    sortConfig: { sortBy: string | null; sortOrder: 'asc' | 'desc' };
    onSortChange: (columnId: string) => void;
    onLeadClick: (candidate: AtsCandidate) => void;
    selectedCandidates: string[];
    onSelectionChange: (candidateIds: string[]) => void;
}

const AtsLeadsTable: React.FC<AtsLeadsTableProps> = ({
    leads,
    totalProfiles,
    currentPage,
    setCurrentPage,
    sortConfig,
    onSortChange,
    onLeadClick,
    selectedCandidates,
    onSelectionChange,
}) => {
    const leadsPerPage = 10;
    const [currentCandidate, setCurrentCandidate] = useState<AtsCandidate | null>(null);

    const {
        showResumeModal,
        signedResumeUrl,
        isDocx,
        loading,
        docxContainerRef,
        handleViewResume,
        handleDownloadResume,
        handleCloseResumeModal,
    } = useResumeHandler();

    const onViewResume = (candidate: AtsCandidate, e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentCandidate(candidate);
        handleViewResume(candidate);
    };

    const onDownloadResume = (candidate: AtsCandidate, e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentCandidate(candidate);
        handleDownloadResume(candidate);
    };

    const onCloseModal = () => {
        handleCloseResumeModal();
        setCurrentCandidate(null);
    };

    // Selection handlers
    const handleSelectCandidate = (candidateId: string, isAlreadyAdded: boolean) => {
        if (isAlreadyAdded) return; // Don't allow selection of already-added candidates

        if (selectedCandidates.includes(candidateId)) {
            onSelectionChange(selectedCandidates.filter(id => id !== candidateId));
        } else {
            onSelectionChange([...selectedCandidates, candidateId]);
        }
    };

    const handleSelectAll = () => {
        const selectableCandidates = leads.filter(lead => !lead.added_into_job);
        const selectableIds = selectableCandidates.map(lead => lead.id);

        if (selectedCandidates.length === selectableIds.length) {
            // Deselect all
            onSelectionChange([]);
        } else {
            // Select all selectable
            onSelectionChange(selectableIds);
        }
    };

    const isSelected = (candidateId: string) => selectedCandidates.includes(candidateId);

    return (
        <>
            <LeadsTable
                leads={leads}
                showCheckbox={true}
                selectedUsernames={selectedCandidates}
                onSelectLead={(candidateId: string, checked: boolean) => {
                    const candidate = leads.find(l => l.id === candidateId);
                    if (candidate?.added_into_job) return; // Don't allow selection of already-added

                    if (checked) {
                        onSelectionChange([...selectedCandidates, candidateId]);
                    } else {
                        onSelectionChange(selectedCandidates.filter(id => id !== candidateId));
                    }
                }}
                onSelectAll={(checked: boolean) => {
                    if (checked) {
                        const selectableIds = leads.filter(l => !l.added_into_job).map(l => l.id);
                        onSelectionChange(selectableIds);
                    } else {
                        onSelectionChange([]);
                    }
                }}
                columns={[
                    {
                        id: "sequence",
                        label: "#",
                        render: (candidate: AtsCandidate, index?: number) => (
                            <span style={{ fontWeight: 500, color: "#666" }}>
                                {(currentPage - 1) * leadsPerPage + (index ?? 0) + 1}
                            </span>
                        ),
                    },
                    {
                        id: "first_name",
                        label: "Name",
                        sortable: true,
                        render: (candidate: AtsCandidate) => (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor: "pointer",
                                    color: "#5175e0",
                                    fontWeight: "bold",
                                }}
                                onClick={() => onLeadClick(candidate)}
                            >
                                {candidate.added_into_job && (
                                    <Tooltip title="Already added to pool" arrow>
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                backgroundColor: "#2e7d32",
                                                color: "white",
                                                fontSize: "0.75rem",
                                                fontWeight: "bold",
                                                flexShrink: 0,
                                            }}
                                        >
                                            ✓
                                        </span>
                                    </Tooltip>
                                )}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {`${candidate.first_name} ${candidate.last_name}`}
                                </span>
                            </div>
                        ),
                    },
                    {
                        id: "email",
                        label: "Email",
                        sortable: true,
                        render: (candidate: AtsCandidate) =>
                            candidate.email_addresses?.[0]?.value || "N/A",
                    },
                    {
                        id: "title",
                        label: "Title",
                        render: (candidate: AtsCandidate) => (
                            <Tooltip title={candidate.title || ""} arrow>
                                <span>
                                    {candidate.title?.length > 50
                                        ? `${candidate.title.substring(0, 50)}...`
                                        : candidate.title}
                                </span>
                            </Tooltip>
                        ),
                    },
                    {
                        id: "candidate_score",
                        label: "Match Score",
                        sortable: true,
                        render: (candidate: AtsCandidate) => (
                            <span style={{ fontWeight: 500 }}>
                                {candidate.evaluation_details?.candidate_score
                                    ? `${Math.round(candidate.evaluation_details.candidate_score)}%`
                                    : 'N/A'}
                            </span>
                        ),
                    },
                    {
                        id: "potential",
                        label: "Lead Potential",
                        render: (candidate: AtsCandidate) => (
                            <PotentialTag
                                potential={
                                    candidate.evaluation_details?.candidate_score > 80
                                        ? "High"
                                        : candidate.evaluation_details?.candidate_score >= 50
                                            ? "Med"
                                            : "Low"
                                }
                            />
                        ),
                    },
                    {
                        id: "resume_actions",
                        label: "Resume",
                        render: (candidate: AtsCandidate) => (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title={!candidate.resume_url ? "No resume available" : "View Resume"} arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!candidate.resume_url}
                                            onClick={(e) => onViewResume(candidate, e)}
                                            sx={{
                                                color: candidate.resume_url ? "#5175e0" : "#9e9e9e",
                                                "&:hover": {
                                                    bgcolor: candidate.resume_url ? "rgba(81, 117, 224, 0.08)" : "transparent",
                                                },
                                                "&.Mui-disabled": {
                                                    color: "#9e9e9e",
                                                },
                                            }}
                                        >
                                            <Visibility sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={!candidate.resume_url ? "No resume available" : "Download Resume"} arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!candidate.resume_url}
                                            onClick={(e) => onDownloadResume(candidate, e)}
                                            sx={{
                                                color: candidate.resume_url ? "#5175e0" : "#9e9e9e",
                                                "&:hover": {
                                                    bgcolor: candidate.resume_url ? "rgba(81, 117, 224, 0.08)" : "transparent",
                                                },
                                                "&.Mui-disabled": {
                                                    color: "#9e9e9e",
                                                },
                                            }}
                                        >
                                            <Download sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        ),
                    },
                ]}
                totalProfiles={totalProfiles}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                sortConfig={sortConfig}
                onSortChange={onSortChange}
                highlightCondition={(lead: AtsCandidate) => lead.added_into_job === true}
            />

            {/* Resume Modal */}
            <Modal
                open={showResumeModal}
                onClose={onCloseModal}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: 700,
                        background: '#ffffff',
                        padding: 3,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        overflowY: 'auto',
                        maxHeight: '90vh',
                    }}
                >
                    <IconButton
                        onClick={(e) => currentCandidate && onDownloadResume(currentCandidate, e)}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: '#325cd6',
                            zIndex: 1,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#1a3f98',
                            },
                        }}
                    >
                        <Download />
                    </IconButton>
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress />
                        </Box>
                    )}
                    {!isDocx ? (
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                            <Viewer fileUrl={signedResumeUrl || ''} />
                        </Worker>
                    ) : (
                        <Box sx={{
                            '& .docx-wrapper': {
                                flexFlow: 'initial !important',
                                alignItems: 'initial !important',
                            }
                        }}>
                            <div ref={docxContainerRef} style={{ padding: '20px' }} />
                        </Box>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default AtsLeadsTable;
